// ONE AGE FORMAT (Cowork run 33, D5).
//
// The NUMBER is the server's: `age_display` comes from
// `facility/taxon_rules.age_display`, the one ladder — under 14 days in
// days ("9d"), under 8 weeks in weeks ("6w"), under 24 months in months
// ("20m"), then years ("2y") — WHATEVER THE STAGE. The grid cell prints
// that short form; its tooltip and the details panel SPELL THE UNIT OUT
// ("20 months · terrestrial"). Nothing here re-derives the number from
// `age_days`: a second ladder on the client is how "891d · tadpole" sat
// beside "2y · terrestrial" in one column.

const UNITS = {
  d: ['day', 'days'],
  w: ['week', 'weeks'],
  m: ['month', 'months'],
  y: ['year', 'years'],
};

/**
 * "20m" -> "20 months", "1w" -> "1 week". Anything that is not the short
 * shape is returned as it came (a legacy "8 mo", a fixture's "19 months"),
 * so a value the ladder did not write is never mangled.
 */
export const spellAge = (short) => {
  const text = short == null ? '' : String(short).trim();
  const match = /^(\d+)([dwmy])$/.exec(text);
  if (!match) return text;
  const n = Number(match[1]);
  const [one, many] = UNITS[match[2]];
  return `${n} ${n === 1 ? one : many}`;
};

/**
 * The age and the stage for a row: "20m · terrestrial" (short) or
 * "20 months · terrestrial" (`{ long: true }`). Null when there is no age.
 */
// THE WORD FOR AN ANIMAL WHOSE STAGE NO DATE SAYS (Cowork run 46). A
// declared mirror of `facility/taxon_rules.STAGE_NOT_SET`: QADELETE grid B
// (an animal with no birth, hatch or out-of-water date) read "Eggs" in the
// racks drawer while its Feeding History row said "Adult". The server's
// `stage_label` says "Stage not set" for it; surfaces print it MUTED.
export const STAGE_NOT_SET = 'Stage not set';

export const isStageNotSet = (label) => label === STAGE_NOT_SET;

/** What a "Stage not set" badge says on hover — why, and where to fix it. */
export const STAGE_NOT_SET_TITLE = 'Stage not set — no birth, hatch or '
  + 'out-of-water date is recorded. Add one in the animal\'s panel on the '
  + 'Animals page.';

const DATE_FIELDS = ['birth_date', 'hatch_date', 'out_of_water_date'];
const COARSE_EGG_STAGES = new Set(['egg', 'eggs']);

/**
 * THE STAGE WORD FOR A ROW, or null — the server's `stage_label`
 * (`taxon_rules.stage_label`, the ONE display word). An OLDER API without
 * it answers null here and the caller keeps its own coarse fallback,
 * except for the one shape `stage_label` exists to correct: an ANIMAL (never
 * a clutch row) with no date at all, which the coarse ladder calls "eggs" —
 * that reads "Stage not set" whichever API answered.
 */
export const stageWordFor = (record) => {
  if (!record) return null;
  if (record.stage_label) return String(record.stage_label);
  if (!isClutchRowShape(record)
      && COARSE_EGG_STAGES.has(String(record.stage || '').toLowerCase())
      && DATE_FIELDS.every((f) => !record[f])) {
    return STAGE_NOT_SET;
  }
  return null;
};

/**
 * THE STAGE WORD A CLUTCH ROW PRINTS BESIDE ITS AGE, or null (Cowork run
 * 52, C): the Clutches page wrote "13d · egg" while the Animals grid wrote
 * "13d · Egg" for the same reptile. The server's `stage_label` (a listed
 * reptile clutch's band word) when it sends one, else the gecko ladder's
 * egg rung capitalised — the animal's own word. Every other clutch keeps
 * its coarse stage exactly (null here), so frog clutches are unchanged.
 */
export const clutchStageWord = (clutch) => {
  const server = stageWordFor(clutch);
  if (server) return server;
  return String(clutch?.stage || '').trim().toLowerCase() === 'egg' ? 'Egg' : null;
};

// ⚠️ `stageLabel` (Cowork run 45 review): a surface that prints the
// server's `stage_label` beside the age ("Morphling") passes it here so its
// tooltip says the SAME word — the racks drawer's badge read "Morphling"
// while its age tooltip still said "· terrestrial". Absent, the coarse
// `stage` is printed exactly as before.
export const ageLine = (record, { long = false, stageLabel = null } = {}) => {
  if (!record || !record.age_display) return null;
  const age = long ? spellAge(record.age_display) : String(record.age_display);
  const word = stageLabel || record.stage;
  return `${age}${word ? ` · ${word}` : ''}`;
};

// THE DATES AN AGE IS DERIVED FROM, BY KIND OF RECORD. A clutch's first
// date is its LAY date (`birth_date` on the row); an animal's is its birth.
const AGE_DATES = {
  animal: [['Birth', 'birth_date'], ['Hatch', 'hatch_date'], ['Out of water', 'out_of_water_date']],
  clutch: [['Lay', 'birth_date'], ['Hatch', 'hatch_date'], ['Out of water', 'out_of_water_date']],
};

/**
 * THE AGE TOOLTIP — ONE RULE, every surface that prints an age (Cowork run
 * 34, E5): the age and stage SPELLED OUT first ("13 days · egg"), then the
 * dates the number came from ("Lay: 09/24/26 · Hatch: N/A · Out of water:
 * N/A"). The Animals grid's cell, the Clutches grid's cell and the racks
 * page's "Who's inside" rows all read it, so no surface shows a bare "13d"
 * whose unit and stage the keeper has to guess. A record with no age keeps
 * the dates alone.
 *
 * `formatDate` is the caller's own (each grid already has one); a missing
 * date is whatever it answers for an empty value.
 */
// ⚠️ THE ROW SAYS WHAT IT IS (Cowork run 35, G4a): a clutch's tooltip
// read "Birth:" — a caller passed the default kind over a clutch row. A
// clutch row carries the fields only a clutch has (`current_count` /
// `is_split`), so the label is read off the ROW and a clutch says "Lay:"
// whatever the caller passed.
function isClutchRowShape(record) {
  return !!record && typeof record === 'object'
    && ('current_count' in record || 'is_split' in record);
}
const isClutchRow = isClutchRowShape;

// ⚠️ ONE LABEL FOR THE FIRST DATE, ON THE LINE AND IN THE TOOLTIP (Cowork
// run 36, H5). The racks drawer's line read "Laid: 09/24/26" for an egg
// while its tooltip read "Birth: 09/24/26" — the line's own ladder
// (`rackHelpers.getReferenceDateLabel`) and this table disagreed about one
// date. The first date is a LAY date for a clutch and for an animal still
// at the egg stage (`taxon_rules.EGG_STAGES`: "egg", "eggs"), and a BIRTH
// date for anything else; every surface asks this.
const EGG_STAGES = new Set(['egg', 'eggs']);

// ⚠️ A REPTILE'S FIRST DATE IS ALWAYS ITS LAY DATE (operator ruling
// 2026-10-17): *"there should be a lay date and a hatch date"*. The server
// says which rows are reptiles (`is_reptile`, `taxon_rules.is_reptile` — a
// gecko-type species or a listed reptile), so a hatched leopard gecko
// reads "Lay: … · Hatch: …", never "Birth:". A row without the flag keeps
// the stage rule below exactly.
export const isReptileRecord = (record) => !!record && record.is_reptile === true;

export const firstDateLabel = (record, { kind = 'animal' } = {}) => {
  if (isClutchRow(record) || kind === 'clutch') return 'Lay';
  if (isReptileRecord(record)) return 'Lay';
  // An animal the SERVER says no date places is not known to be an egg
  // (Cowork run 46) — read off `stage_label` alone, so a bare `{stage}`
  // row keeps the egg rule exactly as before.
  if (isStageNotSet(record?.stage_label)) return 'Birth';
  const stage = String(record?.stage || '').trim().toLowerCase();
  return EGG_STAGES.has(stage) ? 'Lay' : 'Birth';
};

/**
 * R-OBLIGATE OB-1 (2026-10-31) — A FOUND CLUTCH HAS NO LAY DATE. A tadpole
 * whose lay nobody saw is a clutch with `birth_date: null`; every surface
 * that shows a clutch's lay says "lay not seen" — never a blank, never
 * "Invalid Date", never today. Read off an explicit null (the server's
 * answer), so a row from an older shape that simply lacks the key reads as
 * it always did.
 */
export const LAY_NOT_SEEN = 'lay not seen';
export const isLayNotSeen = (clutch) => !!clutch && clutch.birth_date === null;

export const ageTitle = (record, {
  kind = 'animal', formatDate = (d) => d || 'N/A', stageLabel = null,
} = {}) => {
  if (!record) return '';
  const rowKind = isClutchRow(record) ? 'clutch' : kind;
  const first = firstDateLabel(record, { kind: rowKind });
  const dates = (AGE_DATES[rowKind] || AGE_DATES.animal)
    .map(([label, field]) => [field === 'birth_date' ? first : label, field])
    .map(([label, field]) => ((rowKind === 'clutch' && field === 'birth_date'
      && isLayNotSeen(record))
      ? LAY_NOT_SEEN
      : `${label}: ${formatDate(record[field])}`));
  const age = ageLine(record, { long: true, stageLabel });
  // An animal with no date has no age; its tooltip still leads with the
  // badge's word, so "Stage not set" is said where the keeper hovers.
  const lead = age || (isStageNotSet(stageLabel) ? STAGE_NOT_SET : null);
  // R-STAGE-BASIS (2026-10-28): what the stage COUNTS FROM and when it
  // changes, right after the age line — the server's sentence
  // (`taxon_rules.stage_basis`), never derived here.
  const basis = stageBasisSentence(record);
  return [...(lead ? [lead] : []), ...(basis ? [basis] : []), ...dates].join(' · ');
};

/**
 * THE STAGE BASIS SENTENCE for a row, or null (R-STAGE-BASIS, Fable 5.1
 * with the operator, 2026-10-28): "Subadult, counted from out of water
 * 10/12/2025. Adult from 10/13/2026." — `stage_basis.sentence` off the
 * server (`facility/taxon_rules.stage_basis`, computed by the band
 * function that picked the stage word). An older API without it answers
 * null and every surface reads as it did.
 */
export const stageBasisSentence = (record) => {
  const sentence = record && record.stage_basis && record.stage_basis.sentence;
  return sentence ? String(sentence) : null;
};

/**
 * "Ships from 4/12/2027" (R-SHIP-AFTER, 2026-10-28) — the server's
 * `ships_from_note`, sent only while that day is still in the future; null
 * otherwise. Never a refusal: the row can be listed; it cannot ship yet.
 */
export const shipsFromNote = (record) => {
  const note = record && record.ships_from_note;
  return note ? String(note) : null;
};
